import { Box, Tab, Tabs } from "@mui/material";
import { SimpleToolbar } from "../../../components/layout/Toolbar";
import { MenuDropDown } from "../../../components/common/MenuDropDown";
import type { MenuDropDownItem } from "../../../components/common/MenuDropDown";
import { CustomIconButton } from "../../../components/common/customIconButton";
import { NotificationBellButton } from "@/components/notifications/NotificationBell";
import { DataCompletenessButton } from "@/components/dataCompleteness/DataCompletenessButton";
import chatIcon from "@/assets/svg/notes.svg";
import pencilIcon from "@/assets/svg/edit.svg";
import clockIcon from "@/assets/svg/clock.png";

export type DealDetailTabId =
  | "deal"
  | "statusHistory"
  | "sponsors"
  | "contacts"
  | "accordions"
  | "assets"
  | "tenants"
  | "files"
  | "advisorFees"
  | "dealComps"
  | "nda"
  | "gallery";

type DealDetailToolbarProps = {
  activeTab: DealDetailTabId;
  onActiveTabChange: (tab: DealDetailTabId) => void;
  tabNavItems: { id: DealDetailTabId; label: string }[];
  canEdit: boolean;
  dealMenuItems: MenuDropDownItem[];
  onOpenNotes: () => void;
  onOpenActivity: () => void;
  onEdit: () => void;
};

export function DealDetailToolbar({
  activeTab,
  onActiveTabChange,
  tabNavItems,
  canEdit,
  dealMenuItems,
  onOpenNotes,
  onOpenActivity,
  onEdit,
}: DealDetailToolbarProps) {
  return (
    <Box
      sx={{
        padding: "0px",
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: "background.paper",
        boxShadow: "0px 1px 5px 0px rgba(44, 62, 80, 0.12)",
        "& .toolbar": {
          top: 0,
          alignItems: "center",
          flexWrap: "nowrap",
          minWidth: 0,
          maxWidth: "100%",
        },
        "& .toolbar-left": {
          flex: "1 1 10%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          // Toolbar.css uses 10px padding; that stacks with tab min-height and reads as a “fat” bar
          padding: "4px 8px",
          flexWrap: "nowrap",
          alignItems: "center",
        },
        "& .toolbar-right": {
          flex: "0 0 auto",
          width: "auto",
          maxWidth: "none",
          marginLeft: "0 !important",
          flexShrink: 0,
          justifyContent: "flex-end",
          paddingRight: "15px",
        },
      }}
    >
      <SimpleToolbar customToolbarPadding="0px 0px">
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            alignSelf: "center",
            display: "flex",
            padding: 0,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => onActiveTabChange(value as DealDetailTabId)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{ className: "tf-tabs__indicator" }}
            sx={{
              width: "100%",
              minWidth: 0,
              minHeight: 0,
              "& .MuiTabs-flexContainer": { alignItems: "center" },
              "& .MuiTabScrollButton-root": { width: 15 },
              // Slightly under tabs.css tf-tab--compact (44px) for this sticky toolbar
              "& .MuiTab-root.tf-tab.tf-tab--compact": {
                minHeight: "43px !important",
                py: 1,
              },
            }}
          >
            {tabNavItems.map(({ id, label }) => (
              <Tab
                key={id}
                id={`deal-tab-${id}`}
                value={id}
                label={label}
                aria-controls={`deal-tabpanel-${id}`}
                className="tf-tab tf-tab--compact"
              />
            ))}
          </Tabs>
        </Box>

        <Box
          data-position="right"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            ml: -0.5,
            "& .MuiIconButton-root": {
              p: 0.6,
            },
            "& .MuiIconButton-root .MuiSvgIcon-root": {
              fontSize: 22,
            },
          }}
        >
          {canEdit && (
            <MenuDropDown
              title="Deal Menu"
              tooltip="Menu"
              menuItems={dealMenuItems}
            />
          )}
          <CustomIconButton
            tooltip="Notes & Tasks"
            icon={
              <Box
                component="img"
                src={chatIcon}
                alt="Open notes and tasks"
                sx={{
                  width: 20,
                  height: 20,
                  display: "block",
                  marginLeft: "5px",
                }}
              />
            }
            onClick={onOpenNotes}
          />
          <DataCompletenessButton />
          <NotificationBellButton />
          <CustomIconButton
            tooltip="Activity"
            icon={
              <Box
                component="img"
                src={clockIcon}
                alt="Open activity"
                sx={{ width: 20, height: 20, display: "block" }}
              />
            }
            onClick={onOpenActivity}
          />
          <CustomIconButton
            tooltip="Edit"
            icon={
              <Box
                component="img"
                src={pencilIcon}
                alt="Edit deal"
                sx={{ width: 20, height: 20, display: "block" }}
              />
            }
            disabled={!canEdit}
            onClick={onEdit}
          />
        </Box>
      </SimpleToolbar>
    </Box>
  );
}
